import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import './FameRecognitionCinematic.css';
import { DiceRollTumble } from './DiceRollTumble';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';

/**
 * A8r Slice C — Fame Recognition cinematic.
 *
 * Two-phase visual reveal for a freshly-rolled Fame Recognition outcome:
 *
 *   Phase 1 (~1.0s): DiceRollTumble overlay (selector-mode) — re-uses the
 *                    existing dice tumble component including its SFX +
 *                    A9 atmosphere settings.
 *   Phase 2 (~1.2s): Reveal pane — green "IDENTIFIED" + NPC name for
 *                    recognized=true; dark "UNKNOWN" + ████████ for
 *                    recognized=false.
 *
 * Gated on TWO atmosphere flags:
 *   - `fameRecognitionCinematicEnabled` — Slice C-specific
 *   - `animationsEnabled` — master switch
 * When either is OFF, the cinematic renders nothing and fires onComplete
 * immediately so the parent (ScenarioRunner) can clear its pending state.
 *
 * Fired by ScenarioRunner via the `pendingFameReveal` state slot, which
 * is set by the `useFameRecognition` hook's `onRollFired` callback (which
 * only fires for NEW rolls, NOT on registry-load mount — see the hook's
 * `freshlyRolledRef` discipline).
 */
export interface FameRecognitionCinematicProps {
  /** NPC display name shown in the reveal pane on recognized=true. */
  npcName: string;
  /** Roll outcome — drives the reveal pane variant + onComplete payload. */
  recognized: boolean;
  /** Fired when the cinematic has fully completed (all phases). */
  onComplete: () => void;
  /** Optional testid prefix; default 'br-fame-recognition-cinematic'. */
  testidPrefix?: string;
  /** 2026-06-04 dice-feedback fix: how long the reveal pane stays
   *  fully-visible after the animate-in completes, before the fade-out.
   *  Default 2500ms gives the GM time to read IDENTIFIED + NPC name + the
   *  TAROT operative recognized subtitle. Tests pass 0 to skip the hold. */
  holdMs?: number;
  /** Override the fade-out duration. Default 0.5s. */
  fadeoutDurationSeconds?: number;
}

type Phase = 'dice' | 'reveal' | 'hold' | 'fadeout';

const REVEAL_DURATION_SECONDS = 1.2;
const DEFAULT_HOLD_MS = 2500;
const DEFAULT_FADEOUT_SECONDS = 0.5;
const REDACTION_GLYPH = '█'.repeat(8); // ████████ (8 full blocks)

export function FameRecognitionCinematic({
  npcName,
  recognized,
  onComplete,
  testidPrefix = 'br-fame-recognition-cinematic',
  holdMs: holdMsProp,
  fadeoutDurationSeconds = DEFAULT_FADEOUT_SECONDS,
}: FameRecognitionCinematicProps) {
  const {
    fameRecognitionCinematicEnabled,
    animationsEnabled,
    cinematicDismissMode,
    cinematicHoldMs,
  } = useAtmosphereSettings();
  const cinematicActive = fameRecognitionCinematicEnabled && animationsEnabled;
  const [phase, setPhase] = useState<Phase>('dice');

  // 2026-06-05 cinematic-linger: the reveal-pane hold now honors the
  // user-controlled cinematicHoldMs floor in auto mode. Explicit
  // `holdMs` prop still wins for tests + future callers. In manual
  // mode, hold-phase NEVER auto-advances to fadeout — only the click
  // handler below does (transitions hold → fadeout, fadeout animation
  // completes, onComplete fires).
  const effectiveHoldMs = holdMsProp ?? Math.max(DEFAULT_HOLD_MS, cinematicHoldMs);
  const isManualDismiss = cinematicDismissMode === 'manual';

  // 2026-06-04 dice-feedback fix: hold the reveal pane fully-visible for
  // `holdMs` after it animates in, THEN trigger a fade-out.
  // 2026-06-05 cinematic-linger: manual mode never auto-fadeouts.
  useEffect(() => {
    if (phase !== 'hold') return;
    if (isManualDismiss) return; // wait for click
    if (effectiveHoldMs <= 0) {
      setPhase('fadeout');
      return;
    }
    const id = window.setTimeout(() => setPhase('fadeout'), effectiveHoldMs);
    return () => window.clearTimeout(id);
  }, [phase, effectiveHoldMs, isManualDismiss]);

  const handleManualDismiss = () => {
    if (!isManualDismiss) return;
    // Only valid during hold (reveal anim still finishing → ignore).
    if (phase !== 'hold') return;
    setPhase('fadeout');
  };

  // R2.C2 fix (2026-06-04): guard against double-fire on the
  // cinematic-disabled path. Inline arrow `onComplete` (e.g.
  // `() => setPendingFameReveal(null)` in ScenarioRunner) has a fresh
  // identity each render. Without the ref guard, calling onComplete
  // would re-render the parent, change the arrow identity, re-trigger
  // the effect, and fire onComplete a second time. The ref ensures the
  // callback fires exactly once per mount.
  const disabledCompleteFiredRef = useRef<boolean>(false);
  useEffect(() => {
    if (cinematicActive) return;
    if (disabledCompleteFiredRef.current) return;
    disabledCompleteFiredRef.current = true;
    onComplete();
  }, [cinematicActive, onComplete]);

  if (!cinematicActive) {
    return null;
  }

  return (
    <div
      className="br-fame-recognition-cinematic-backdrop"
      data-testid={testidPrefix}
      data-dismiss-mode={cinematicDismissMode}
      aria-live="polite"
      aria-label={
        recognized
          ? `Fame Recognition: ${npcName} identified${isManualDismiss ? ' — click to continue' : ''}`
          : `Fame Recognition: operative unknown${isManualDismiss ? ' — click to continue' : ''}`
      }
      onClick={isManualDismiss ? handleManualDismiss : undefined}
      onKeyDown={
        isManualDismiss
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleManualDismiss();
              }
            }
          : undefined
      }
      role={isManualDismiss ? 'button' : undefined}
      tabIndex={isManualDismiss ? 0 : undefined}
      style={isManualDismiss ? { cursor: 'pointer' } : undefined}
    >
      {phase === 'dice' && (
        <DiceRollTumble
          mode="selector"
          durationMs={1000}
          onComplete={() => setPhase('reveal')}
          testidPrefix={`${testidPrefix}-dice`}
        />
      )}
      {(phase === 'reveal' || phase === 'hold' || phase === 'fadeout') && (
        <motion.div
          className={
            recognized
              ? 'br-fame-recognition-reveal br-fame-recognition-recognized'
              : 'br-fame-recognition-reveal br-fame-recognition-unknown'
          }
          data-testid="br-fame-recognition-reveal"
          data-phase={phase}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={
            phase === 'fadeout'
              ? { opacity: 0, scale: 1 }
              : { opacity: 1, scale: 1 }
          }
          transition={{
            duration:
              phase === 'reveal'
                ? REVEAL_DURATION_SECONDS
                : phase === 'fadeout'
                ? fadeoutDurationSeconds
                : 0,
            ease: 'easeOut',
          }}
          onAnimationComplete={() => {
            if (phase === 'reveal') {
              setPhase('hold');
            } else if (phase === 'fadeout') {
              onComplete();
            }
          }}
        >
          <div className="br-fame-recognition-stamp" data-testid="br-fame-recognition-stamp">
            {recognized ? 'IDENTIFIED' : 'UNKNOWN'}
          </div>
          <div className="br-fame-recognition-name">
            {recognized ? npcName : REDACTION_GLYPH}
          </div>
          <div className="br-fame-recognition-subtitle">
            {recognized
              ? 'TAROT operative recognized'
              : 'No positive identification'}
          </div>
          {isManualDismiss && phase === 'hold' && (
            <div
              className="br-fame-recognition-dismiss-hint"
              data-testid="br-fame-recognition-dismiss-hint"
              aria-hidden="true"
            >
              Click to continue ▸
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

export default FameRecognitionCinematic;
